/* ==========================================================================
   Mari & Moss — motion
   ==========================================================================

   Staggered reveals, word-level headline reveals, parallax, header height
   publishing and page-transition fades.

   THERE USED TO BE A SCROLL-RESPONSIVE MARQUEE HERE. An initMarquee() routine
   read scroll velocity and pushed the artwork band along with the page via a
   --mm-marquee-push custom property. Removed 2026-08-14: Sean wanted the band
   to scroll by itself and not react to the page. The matching
   `transform: translate3d(var(--mm-marquee-push, 0px), 0, 0)` came off
   .mm-marquee__tile in assets/mm-marquee.css at the same time. Both halves go
   together — either one left behind is dead weight that still costs a scroll
   listener and a rAF loop on every page.

   TWO RULES THIS FILE OBEYS.

   1. Nothing here may be load-bearing for content being visible. The hidden
      states live behind the `.mm-motion` class, which layout/theme.liquid adds
      inline before first paint and removes again after 2s if this file never
      signals ready. If this script 404s, fails to parse, or throws, the page
      renders complete and static.

   2. Header height publishing runs even when motion is off, because the
      transparent-header overlap on the homepage depends on it.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var motion = root.classList.contains('mm-motion');

  /* ------------------------------------------------------------------------
     Header height

     Published as --mm-header-h and consumed by mm-header.css to pull
     #MainContent up under the transparent header, and by mm-commerce.css to
     offset the sticky product rail.

     The previous implementation used Dawn's --header-height, which is only
     recalculated on a matchMedia breakpoint change — so a nav that wrapped or
     a webfont that landed late left the value stale and the overlap wrong. A
     ResizeObserver on the element itself cannot go stale.
     ------------------------------------------------------------------------ */

  /* Held so a re-init (the theme editor fires shopify:section:load on every
     edit) can tear down the previous observer instead of stacking a new one on
     top of it. */
  var headerObserver = null;

  function initHeaderHeight() {
    var header = document.querySelector('.section-header');
    if (!header) return;

    var apply = function () {
      var h = header.getBoundingClientRect().height;
      if (h > 0) root.style.setProperty('--mm-header-h', h + 'px');
    };

    apply();

    if (window.ResizeObserver) {
      if (headerObserver) headerObserver.disconnect();
      headerObserver = new ResizeObserver(apply);
      headerObserver.observe(header);
    } else {
      window.addEventListener('resize', apply, { passive: true });
    }

    /* Cormorant changes the wordmark's line box when it swaps in. The observer
       catches this on its own; this is belt and braces for the first paint. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(apply).catch(function () {});
    }
  }

  /* ------------------------------------------------------------------------
     Reveals

     REVEAL_SELECTOR must stay in step with the matching list in mm-base.css.
     The hidden state has to be declared in CSS rather than applied here, or
     the browser paints the content once before this script hides it — the
     flash is worse than having no animation at all.

     The hero is deliberately absent from the list. It is the first thing a
     visitor sees and the page's LCP element; fading it in delays the largest
     paint for no benefit.
     ------------------------------------------------------------------------ */

  var REVEAL_SELECTOR = [
    '[data-mm-reveal]',
    '.collection-list__item',
    '.product-grid > .grid__item',
    '.collection__title',
    '.collection-list-wrapper .title-wrapper-with-link',
    '.collage__item',
    '.collage-wrapper-title',
    '.multirow .image-with-text',
    '.article-card-wrapper',
    '.mm-marquee__head',
    '.related-products__heading',
    '.main-page-title',
    '.article-template__title',
    '.footer-block',
    '.footer-block--newsletter'
  ].join(',');

  var STAGGER_MS = 90;
  var STAGGER_CAP = 5;

  function initReveals(scope) {
    var nodes = (scope || document).querySelectorAll(REVEAL_SELECTOR);
    if (!nodes.length) return;

    if (!window.IntersectionObserver) {
      /* No observer support: show everything immediately rather than leaving
         it hidden. */
      Array.prototype.forEach.call(nodes, function (n) {
        n.classList.add('is-revealed');
      });
      return;
    }

    /* Stagger siblings so a grid of cards arrives as a sequence rather than
       all at once. Counted per parent, and capped so a 24-product grid does
       not end up with a two-second tail. */
    var counters = new Map();
    Array.prototype.forEach.call(nodes, function (n) {
      var parent = n.parentNode;
      var i = counters.get(parent) || 0;
      counters.set(parent, i + 1);
      if (i > 0) {
        n.style.setProperty('--mm-reveal-delay', Math.min(i, STAGGER_CAP) * STAGGER_MS + 'ms');
      }
    });

    var pending = new Set(nodes);

    function reveal(el) {
      el.classList.add('is-revealed');
      pending.delete(el);
      io.unobserve(el);
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.01 }
    );

    Array.prototype.forEach.call(nodes, function (n) {
      io.observe(n);
    });

    /* THE SAFETY SWEEP.

       IntersectionObserver only reports threshold crossings it actually
       samples. Jump from the top of the page to the bottom in one step — an
       anchor link, a scrollbar drag, a restored scroll position, Cmd+End, or a
       full-page screenshot — and every element in between goes from below the
       viewport to above it between two frames. The ratio reads 0 both times,
       no crossing is detected, and those elements stay hidden permanently.

       This was not hypothetical: it left the category row, the featured
       products, the sketchbook collage and most of the artwork panel invisible
       on a real page.

       The sweep runs after scrolling settles and reveals anything whose top
       has already passed the bottom of the viewport, so content can never be
       stranded. It only ever walks the not-yet-revealed set, which shrinks to
       nothing. */
    var sweepTimer = null;

    function sweep() {
      sweepTimer = null;
      if (!pending.size) {
        window.removeEventListener('scroll', onScrollSweep);
        window.removeEventListener('resize', onScrollSweep);
        return;
      }
      var limit = window.innerHeight;
      Array.prototype.forEach.call(Array.from(pending), function (el) {
        if (el.getBoundingClientRect().top < limit) reveal(el);
      });
    }

    function onScrollSweep() {
      if (sweepTimer) window.clearTimeout(sweepTimer);
      sweepTimer = window.setTimeout(sweep, 140);
    }

    window.addEventListener('scroll', onScrollSweep, { passive: true });
    window.addEventListener('resize', onScrollSweep, { passive: true });
  }

  /* ------------------------------------------------------------------------
     Word-level headline reveal

     Wraps words in spans and leaves the text itself untouched, so screen
     readers, text selection and find-in-page all behave exactly as before —
     no aria-label substitution, no text moved into pseudo-elements.

     Applied only to elements carrying data-mm-split, which today is the
     artwork panel heading. That heading is below the fold, so hiding it until
     this script runs costs nothing.
     ------------------------------------------------------------------------ */

  function splitWords(node) {
    var children = Array.prototype.slice.call(node.childNodes);
    children.forEach(function (child) {
      if (child.nodeType === 3) {
        var text = child.nodeValue;
        if (!text.trim()) return;
        var fragment = document.createDocumentFragment();
        text.split(/(\s+)/).forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }
          var span = document.createElement('span');
          span.className = 'mm-word';
          span.textContent = part;
          fragment.appendChild(span);
        });
        child.parentNode.replaceChild(fragment, child);
      } else if (child.nodeType === 1) {
        /* Recurse so an <em> accent word keeps its italics. */
        splitWords(child);
      }
    });
  }

  function initSplitHeadings(scope) {
    var headings = (scope || document).querySelectorAll('[data-mm-split]');
    if (!headings.length) return;

    Array.prototype.forEach.call(headings, function (heading) {
      if (heading.dataset.mmSplitDone) return;
      heading.dataset.mmSplitDone = 'true';

      splitWords(heading);

      var words = heading.querySelectorAll('.mm-word');
      Array.prototype.forEach.call(words, function (word, i) {
        word.style.setProperty('--mm-word-delay', i * 70 + 'ms');
      });

      if (!window.IntersectionObserver) {
        heading.classList.add('is-revealed');
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );
      io.observe(heading);

      /* Same stranding risk as the reveal sweep above: a heading scrolled past
         between two frames never registers a crossing and its words stay
         hidden. Without this, the artwork panel headline vanished entirely
         whenever the page was jumped rather than scrolled. */
      var timer = null;
      var settle = function () {
        timer = null;
        if (heading.classList.contains('is-revealed')) {
          window.removeEventListener('scroll', onScroll);
          return;
        }
        if (heading.getBoundingClientRect().top < window.innerHeight) {
          heading.classList.add('is-revealed');
          io.unobserve(heading);
          window.removeEventListener('scroll', onScroll);
        }
      };
      var onScroll = function () {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(settle, 140);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ------------------------------------------------------------------------
     Parallax

     Transform only — never top, height or background-position — so it cannot
     trigger layout and cannot change the height of its container. The element
     is inset beyond its panel in CSS (-12%) to give the translate somewhere to
     go; strength is kept below that overscan so an edge can never be exposed.
     ------------------------------------------------------------------------ */

  function initParallax() {
    var layers = document.querySelectorAll('[data-mm-parallax]');
    if (!layers.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var viewport = window.innerHeight;

      Array.prototype.forEach.call(layers, function (layer) {
        var panel = layer.parentElement;
        if (!panel) return;
        var rect = panel.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;

        var strength = parseFloat(layer.getAttribute('data-mm-parallax-strength'));
        if (!strength || strength <= 0) strength = 0.1;

        /* -1 when the panel sits below the fold, +1 when it has passed above. */
        var centre = rect.top + rect.height / 2;
        var progress = (centre - viewport / 2) / (viewport / 2 + rect.height / 2);
        progress = Math.max(-1, Math.min(1, progress));

        var shift = progress * strength * rect.height;
        layer.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0)';
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------------
     Page transitions

     Deliberately conservative. Intercepting clicks is the easiest way in this
     whole file to break a working link, so the handler bails out on anything
     it does not fully understand, and navigation is fired from a timeout that
     runs whether or not the animation finishes.

     Header and cart controls are excluded outright: the cart drawer, search
     modal and localisation forms all attach their own handlers there.
     ------------------------------------------------------------------------ */

  var FADE_OUT_MS = 150;

  function initPageTransition() {
    document.addEventListener('click', function (event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!event.target || !event.target.closest) return;

      var link = event.target.closest('a[href]');
      if (!link) return;

      /* Anything with its own JavaScript behaviour. */
      if (link.closest('.section-header, .cart-drawer, .facets, predictive-search, .localization-form, [data-mm-no-transition]')) {
        return;
      }
      if (link.hasAttribute('download') || link.hasAttribute('data-no-instant')) return;
      if (link.target && link.target !== '_self') return;
      if (link.getAttribute('href').charAt(0) === '#') return;

      var url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (e) {
        return;
      }

      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      event.preventDefault();
      root.classList.add('is-leaving');
      window.setTimeout(function () {
        window.location.href = link.href;
      }, FADE_OUT_MS);

      /* Escape hatch. `is-leaving` fades the whole of #MainContent to zero, so
         any click that does NOT actually unload the document — a link that
         serves a file, a navigation the browser blocks, a cancelled
         beforeunload — would otherwise leave the page permanently blank with no
         way back. If we are still here after 1.5s, the navigation did not
         happen; put the page back. */
      window.setTimeout(function () {
        root.classList.remove('is-leaving');
      }, 1500);
    });

    /* Returning via the back button restores the page from bfcache with the
       leaving class still set, which would leave it faded out. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) root.classList.remove('is-leaving');
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     AJAX-replaced content

     Filtering or sorting a collection makes Dawn's facets.js replace the whole
     of #ProductGridContainer via innerHTML, WITHOUT firing any event. The
     replacement cards are brand new nodes: they carry the hidden state from the
     CSS reveal list, no observer is watching them, and they are not in the
     sweep's pending set — so they would stay at opacity 0 forever and the grid
     would look empty after every filter change.

     Watching the container for child changes and re-running the reveals covers
     that, and any other Dawn component that swaps markup in place.
     ------------------------------------------------------------------------ */

  function initDynamicContent() {
    if (!window.MutationObserver) return;

    var targets = document.querySelectorAll(
      '#ProductGridContainer, #main-collection-product-grid, .cart-drawer, #CartDrawer'
    );
    if (!targets.length) return;

    var pendingRerun = null;
    var observer = new MutationObserver(function () {
      if (pendingRerun) window.clearTimeout(pendingRerun);
      pendingRerun = window.setTimeout(function () {
        pendingRerun = null;
        initReveals();
        initSplitHeadings();
      }, 60);
    });

    Array.prototype.forEach.call(targets, function (t) {
      observer.observe(t, { childList: true, subtree: true });
    });
  }

  function boot() {
    /* Each step is isolated. Without this, a throw anywhere in here would skip
       the ready flag AND skip registering the shopify:section:load listener
       below — the 2s failsafe would still reveal the content, but the theme
       editor would then be permanently broken for the session. */
    var steps = [initHeaderHeight];
    if (motion) {
      steps.push(initReveals, initSplitHeadings, initParallax, initPageTransition, initDynamicContent);
    }

    steps.forEach(function (step) {
      try {
        step();
      } catch (e) {
        if (window.console && console.warn) console.warn('mm-motion: ' + step.name + ' failed', e);
      }
    });

    /* Tells the inline failsafe in theme.liquid that this file is alive, so it
       does not strip the .mm-motion class at the 2s mark. Only claimed once the
       reveals are actually wired up. */
    root.setAttribute('data-mm-motion-ready', 'true');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor re-renders sections in place. Without this, anything the
     merchant edits stays invisible until a full reload. */
  document.addEventListener('shopify:section:load', function (event) {
    initHeaderHeight();
    if (!motion) return;
    initReveals(event.target);
    initSplitHeadings(event.target);
    initParallax();
  });
})();